import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, Form, Input, Switch, InputNumber, Button, Space, message } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { taxonomyAPI } from '../../Utils/taxonomyApi';
import { languagesAPI } from '../../Utils/api';
import { useLanguage } from '../../Context/LanguageContext';

interface Option {
  id: string;
  categoryId: string;
  slug: string;
  value: string;
  displayOrder: number;
  isActive: boolean;
}

const OptionDetailPage: React.FC = () => {
  const { optionId, categoryId } = useParams<{ optionId: string; categoryId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [option, setOption] = useState<Option | null>(null);
  const [category, setCategory] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  // Language Context
  const { currentLanguage, multiLanguageData, setMultiLanguageData } = useLanguage();

  const isNewOption = optionId === 'new';

  useEffect(() => {
    // The language context is shared across pages; drop the previous option's translations
    setMultiLanguageData({});
    if (!isNewOption && optionId) {
      fetchOption();
      fetchTranslations();
    }
    // Load category for breadcrumb/title
    if (categoryId) {
      fetchCategory();
    }
  }, [optionId, categoryId]);

  // Load translations when language changes
  useEffect(() => {
    if (optionId && !isNewOption && option) {
      // Existing option: Load from fetched data
      loadFormDataForLanguage();
    } else if (isNewOption) {
      // New option: Load from multiLanguageData
      loadFormDataForNewOption();
    }
  }, [currentLanguage, option, multiLanguageData]);

  const fetchTranslations = async (id?: string) => {
    const targetId = id || optionId;
    if (!targetId) return;
    
    try {
      const response = await languagesAPI.getTranslations('taxonomyOption', targetId);
      const translationsData = response.data.translations || {};
      setMultiLanguageData(translationsData);
    } catch (error) {
      console.error('Error fetching translations:', error);
    }
  };

  const loadFormDataForLanguage = () => {
    if (currentLanguage === 'de') {
      // Load German (master) data
      form.setFieldsValue({
        value: option?.value || '',
        slug: option?.slug || '',
        displayOrder: option?.displayOrder || 0,
        isActive: option?.isActive || false,
      });
    } else {
      // Load translation data if exists, keep German as placeholder
      const langData = multiLanguageData[currentLanguage] || {};
      form.setFieldsValue({
        value: langData.value || '',
        slug: option?.slug || '',
        displayOrder: option?.displayOrder || 0,
        isActive: option?.isActive || false,
      });
    }
  };

  const loadFormDataForNewOption = () => {
    // For new options, load data from multiLanguageData
    if (currentLanguage === 'de') {
      const deData = multiLanguageData['de'] || {};
      form.setFieldsValue({
        value: deData.value || '',
        slug: deData.slug || '',
        displayOrder: deData.displayOrder || 0,
        isActive: deData.isActive !== undefined ? deData.isActive : true,
      });
    } else {
      const langData = multiLanguageData[currentLanguage] || {};
      const deData = multiLanguageData['de'] || {};
      form.setFieldsValue({
        value: langData.value || '',
        slug: deData.slug || '', // slug is language-independent
        displayOrder: deData.displayOrder || 0,
        isActive: deData.isActive !== undefined ? deData.isActive : true,
      });
    }
  };

  // Track form changes and store in multiLanguageData
  const handleFormChange = (changedValues: any, allValues: any) => {
    if (isNewOption) {
      // For new options, store ALL language data (including DE)
      const translationFields = ['value'];
      const updatedLangData = { ...multiLanguageData[currentLanguage] };
      
      translationFields.forEach(field => {
        if (changedValues[field] !== undefined) {
          updatedLangData[field] = changedValues[field];
        }
      });

      // Also store language-independent fields in DE data
      if (currentLanguage === 'de') {
        ['slug', 'displayOrder', 'isActive'].forEach(field => {
          if (changedValues[field] !== undefined) {
            updatedLangData[field] = changedValues[field];
          }
        });
      }
      
      setMultiLanguageData({
        ...multiLanguageData,
        [currentLanguage]: updatedLangData,
      });
    } else if (currentLanguage !== 'de') {
      // For existing options, only store non-German translations
      const translationFields = ['value'];
      const updatedLangData = { ...multiLanguageData[currentLanguage] };
      
      translationFields.forEach(field => {
        if (changedValues[field] !== undefined) {
          updatedLangData[field] = changedValues[field];
        }
      });
      
      setMultiLanguageData({
        ...multiLanguageData,
        [currentLanguage]: updatedLangData,
      });
    }
  };

  const fetchOption = async () => {
    if (!optionId || isNewOption) return;
    setLoading(true);
    try {
      const response = await taxonomyAPI.getOption(optionId);
      const opt = response.data.option || response.data;
      setOption(opt);
      form.setFieldsValue(opt);
      
      // Fetch category if we have categoryId from option
      if (opt.categoryId && !category) {
        fetchCategory(opt.categoryId);
      }
    } catch (error) {
      message.error('Fehler beim Laden der Option');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategory = async (catId?: string) => {
    const targetCatId = catId || categoryId;
    if (!targetCatId) return;
    
    try {
      const response = await taxonomyAPI.getCategory(targetCatId);
      setCategory(response.data.category || response.data);
    } catch (error) {
      console.error('Error fetching category:', error);
    }
  };

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      // CRITICAL: Separate German base data from translations
      const payload: any = {};

      // If user is currently editing in non-German language
      let translationsToSave = { ...multiLanguageData };

      if (isNewOption) {
        // NEW OPTION: Use multiLanguageData for German base
        const deData = multiLanguageData['de'] || {};
        payload.value = deData.value || values.value || '';
        payload.slug = values.slug || deData.slug;
        payload.displayOrder = values.displayOrder !== undefined ? values.displayOrder : (deData.displayOrder || 0);
        payload.isActive = values.isActive !== undefined ? values.isActive : (deData.isActive !== undefined ? deData.isActive : true);

        // Store current form data if in non-German mode
        if (currentLanguage !== 'de') {
          const currentLangData: any = {};
          if (values.value && values.value.trim()) {
            currentLangData.value = values.value;
          }

          if (Object.keys(currentLangData).length > 0) {
            translationsToSave = {
              ...translationsToSave,
              [currentLanguage]: currentLangData,
            };
          }
        } else {
          // Update DE data with current form values
          translationsToSave = {
            ...translationsToSave,
            'de': {
              value: payload.value,
              slug: payload.slug,
              displayOrder: payload.displayOrder,
              isActive: payload.isActive,
            }
          };
        }
      } else {
        // EXISTING OPTION
        if (currentLanguage !== 'de') {
          // User is editing in non-German mode
          // Store current form edits as translation for current language
          const currentLangData: any = {};
          if (values.value && values.value.trim()) {
            currentLangData.value = values.value;
          }

          if (Object.keys(currentLangData).length > 0) {
            translationsToSave = {
              ...translationsToSave,
              [currentLanguage]: currentLangData,
            };
          }

          // Use original option data for German base (prevent overwriting German with EN)
          payload.value = option?.value || '';
        } else {
          // User is in German mode - use form values for base data
          payload.value = values.value;
        }

        // Always include these fields from form (language-independent)
        payload.slug = values.slug;
        payload.displayOrder = values.displayOrder;
        payload.isActive = values.isActive;
      }

      // Add translations if we have any non-German data
      const hasTranslations = Object.keys(translationsToSave).some(
        lang => lang !== 'de' && translationsToSave[lang] && Object.keys(translationsToSave[lang]).length > 0
      );

      if (hasTranslations) {
        payload.translations = {};
        Object.keys(translationsToSave).forEach(lang => {
          if (lang !== 'de' && translationsToSave[lang]) {
            const langData = translationsToSave[lang];
            const hasData = Object.values(langData).some(val => val && String(val).trim());
            if (hasData) {
              payload.translations[lang] = langData;
            }
          }
        });
      }

      if (isNewOption) {
        const response = await taxonomyAPI.createOption(categoryId!, payload);
        message.success('Option erfolgreich erstellt');
        const newOption = response.data.option;
        
        // Update URL to new option ID
        navigate(`/categories/${categoryId}/options/${newOption.id}`, { replace: true });
        
        // CRITICAL: Load the newly created option and its translations
        setOption(newOption);
        
        // Fetch translations with the new option ID
        await fetchTranslations(newOption.id);
        
        // Load form data for current language
        if (currentLanguage === 'de') {
          form.setFieldsValue({
            value: newOption.value || '',
            slug: newOption.slug || '',
            displayOrder: newOption.displayOrder || 0,
            isActive: newOption.isActive || false,
          });
        } else {
          // If we're in non-DE mode, show the translation
          const langData = translationsToSave[currentLanguage] || {};
          form.setFieldsValue({
            value: langData.value || '',
            slug: newOption.slug || '',
            displayOrder: newOption.displayOrder || 0,
            isActive: newOption.isActive || false,
          });
        }
      } else if (optionId) {
        await taxonomyAPI.updateOption(optionId, payload);
        message.success('Option erfolgreich gespeichert');
        
        // Update multiLanguageData state with saved translations
        setMultiLanguageData(translationsToSave);
        
        fetchOption();
        fetchTranslations();
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Fehler beim Speichern der Option');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key: string) => {
    if (key === 'back') {
      // Navigate back to category with options tab active
      navigate(`/categories/${categoryId || option?.categoryId}?tab=options`);
    } else {
      setActiveTab(key);
    }
  };

  const tabItems = [
    {
      key: 'back',
      label: <span><ArrowLeftOutlined /> Zurück zur Kategorie</span>,
      children: null,
    },
    {
      key: 'general',
      label: 'Optionsdaten',
      children: (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          onValuesChange={handleFormChange}
          style={{ maxWidth: 600 }}
          initialValues={{
            isActive: true,
            displayOrder: 0,
          }}
        >
          <Form.Item
            name="value"
            label={`Wert${currentLanguage !== 'de' ? ` (${currentLanguage.toUpperCase()})` : ''}`}
            rules={[{ required: true, message: 'Bitte Wert eingeben' }]}
            tooltip={currentLanguage !== 'de' ? 'Deutsche Übersetzung wird als Placeholder verwendet' : undefined}
          >
            <Input 
              placeholder={
                currentLanguage === 'de' 
                  ? 'z.B. Edelstahl' 
                  : option?.value || 'Deutsche Übersetzung'
              }
            />
          </Form.Item>

          <Form.Item
            name="slug"
            label="Slug (optional)"
            tooltip="Wird automatisch aus dem Wert generiert, falls leer gelassen"
          >
            <Input placeholder="z.B. stainless-steel (optional)" />
          </Form.Item>

          <Form.Item
            name="displayOrder"
            label="Anzeigereihenfolge"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Aktiv"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
              >
                Speichern
              </Button>
              <Button onClick={() => navigate(`/categories/${categoryId || option?.categoryId}?tab=options`)}>
                Abbrechen
              </Button>
            </Space>
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <div>
      <h1 style={{ marginBottom: 8 }}>
        {isNewOption ? 'Neue Option' : option ? `Option: ${option.value}` : 'Option bearbeiten'}
      </h1>
      {category && (
        <p style={{ color: '#666', marginBottom: 24 }}>
          Kategorie: <strong>{category.name}</strong>
        </p>
      )}

      <Tabs 
        items={tabItems} 
        activeKey={activeTab}
        onChange={handleTabChange}
      />
    </div>
  );
};

export default OptionDetailPage;
